import React, { useState } from "react";
import { memberFetch, clearMemberToken } from "../../lib/memberApi";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, AtSign, XOctagon, Mail } from "lucide-react";

/**
 * Self-service account controls: change password, change email, cancel
 * membership. Each control opens a small modal so accidents are unlikely.
 */
export default function MyAccountPanel({ me, refreshMe }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null); // "password" | "email" | "cancel" | null
  const [weeklyBusy, setWeeklyBusy] = useState(false);

  const close = () => setOpen(null);

  const toggleWeekly = async (e) => {
    const next = e.target.checked;
    setWeeklyBusy(true);
    try {
      await memberFetch("/member/account/weekly-email", {
        method: "POST",
        body: JSON.stringify({ opt_in: next }),
      });
      toast.success(next ? "You'll get the Sunday packet email." : "Weekly email turned off.");
      if (refreshMe) await refreshMe();
    } catch (err) {
      toast.error(err.message || "Couldn't update preference");
    } finally {
      setWeeklyBusy(false);
    }
  };

  return (
    <div className="mt-6 border border-zinc-900 bg-[#0F0F0F] p-5" data-testid="my-account-panel">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-3">// My Account</div>

      <label className="flex items-center gap-3 px-3 py-3 border border-zinc-800 bg-[#0A0A0A] mb-3 cursor-pointer" data-testid="weekly-email-row">
        <Mail size={14} className="text-[#E5B43A]" />
        <div className="flex-1">
          <div className="text-sm font-bold">Weekly Sunday Packet</div>
          <div className="text-[11px] text-zinc-500">Get a fresh AI 7-day workout + meal PDF every Sunday night.</div>
        </div>
        <input
          type="checkbox"
          checked={Boolean(me.weekly_email_opt_in)}
          onChange={toggleWeekly}
          disabled={weeklyBusy}
          data-testid="weekly-email-toggle"
          className="w-5 h-5 accent-[#E5B43A]"
        />
      </label>

      <div className="grid sm:grid-cols-3 gap-2">
        <button
          onClick={() => setOpen("password")}
          data-testid="account-open-password"
          className="inline-flex items-center justify-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-300 border border-zinc-800 px-3 py-3 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
        >
          <Lock size={12} /> Change Password
        </button>
        <button
          onClick={() => setOpen("email")}
          data-testid="account-open-email"
          className="inline-flex items-center justify-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-300 border border-zinc-800 px-3 py-3 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
        >
          <AtSign size={12} /> Change Email
        </button>
        <button
          onClick={() => setOpen("cancel")}
          disabled={!me.active}
          data-testid="account-open-cancel"
          className="inline-flex items-center justify-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-red-300 border border-red-400/30 px-3 py-3 hover:bg-red-400/10 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <XOctagon size={12} /> Cancel Membership
        </button>
      </div>

      {open === "password" && <PasswordModal onClose={close} />}
      {open === "email" && <EmailModal currentEmail={me.email} onClose={close} onSaved={refreshMe} />}
      {open === "cancel" && (
        <CancelModal
          onClose={close}
          onCancelled={() => {
            clearMemberToken();
            navigate("/");
          }}
        />
      )}
    </div>
  );
}

function PasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await memberFetch("/member/account/password", {
        method: "POST",
        body: JSON.stringify({ current_password: cur, new_password: next }),
      });
      toast.success("Password updated");
      onClose();
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title="Change Password" onClose={onClose} testId="password-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Current password" type="password" value={cur} onChange={setCur} testId="cur-password" />
        <Input label="New password (min 6 chars)" type="password" value={next} onChange={setNext} testId="new-password" />
        <Submit busy={busy} label="Update Password" testId="submit-password" />
      </form>
    </ModalShell>
  );
}

function EmailModal({ currentEmail, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await memberFetch("/member/account/email", {
        method: "POST",
        body: JSON.stringify({ current_password: pw, new_email: email }),
      });
      toast.success("Email updated");
      if (onSaved) await onSaved();
      onClose();
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title="Change Email" onClose={onClose} testId="email-modal">
      <p className="text-xs text-zinc-500 mb-3">Current: <span className="text-zinc-300">{currentEmail}</span></p>
      <form onSubmit={submit} className="space-y-3">
        <Input label="New email" type="email" value={email} onChange={setEmail} testId="new-email" />
        <Input label="Current password (for security)" type="password" value={pw} onChange={setPw} testId="email-current-pw" />
        <Submit busy={busy} label="Update Email" testId="submit-email" />
      </form>
    </ModalShell>
  );
}

function CancelModal({ onClose, onCancelled }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (confirm.trim().toUpperCase() !== "CANCEL") {
      toast.error("Type CANCEL exactly to confirm");
      return;
    }
    setBusy(true);
    try {
      await memberFetch("/member/account/cancel", { method: "POST" });
      toast.success("Membership cancelled. Sorry to see you go.");
      onCancelled();
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title="Cancel Membership" onClose={onClose} testId="cancel-modal">
      <p className="text-xs text-zinc-400 leading-relaxed mb-4">
        Your access ends immediately, but your account, plans, progress photos, and signed
        waiver are preserved — you can resubscribe any time. Davon will be notified by email.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Input
          label='Type "CANCEL" to confirm'
          value={confirm}
          onChange={setConfirm}
          testId="cancel-confirm"
        />
        <button
          type="submit"
          disabled={busy || confirm.trim().toUpperCase() !== "CANCEL"}
          data-testid="submit-cancel"
          className="w-full bg-red-500/80 text-white font-bold uppercase tracking-[0.2em] py-3 text-xs hover:bg-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {busy ? "Cancelling..." : "Cancel Membership"}
        </button>
      </form>
    </ModalShell>
  );
}

// --- Tiny shared building blocks ---
function ModalShell({ title, onClose, testId, children }) {
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/80 px-3" onClick={onClose} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-zinc-800 bg-[#0F0F0F] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-2xl leading-none" data-testid="modal-close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1 block">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        required
        className="w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:border-[#E5B43A]"
      />
    </label>
  );
}

function Submit({ busy, label, testId }) {
  return (
    <button
      type="submit"
      disabled={busy}
      data-testid={testId}
      className="w-full bg-[#E5B43A] text-black font-bold uppercase tracking-[0.2em] py-3 text-xs hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-40"
    >
      {busy ? "Working..." : label}
    </button>
  );
}
